/**
 * Auth lifecycle, held outside Redux on purpose: the ky hooks in
 * `endpoints/config.ts` need to read and write it, and importing the store
 * there would close the cycle store -> slices -> thunks -> endpoints -> store.
 * Same shape as `utils/navigationService`.
 */
// `authenticated` is written ONLY by AuthProvider once auth0-react settles an
// authenticated session, so it cannot drift from auth0-react's reducer (the
// canonical source for React consumers). The ky hooks only ever read
// `isNavigationLatched()` and `isTerminal()`.
export type AuthPhase =
  | "initializing"
  | "authenticated"
  | "redirecting"
  | "blocked"
  | "failed"
  | "loggingOut";

export type AuthBlockReason =
  | "PRINCIPAL_UNRESOLVED"
  | "PRINCIPAL_INACTIVE"
  | "TOKEN_REJECTED"
  | "CONFIG_INVALID";

export interface AuthStatus {
  phase: AuthPhase;
  reason?: AuthBlockReason;
  detail?: string;
}

type Listener = (status: AuthStatus) => void;

let status: AuthStatus = { phase: "initializing" };
const listeners = new Set<Listener>();

export const getAuthStatus = (): AuthStatus => status;

/**
 * `redirecting` and `loggingOut` are latched: a navigation has been asked for
 * but the document is still alive, so anything that would start another
 * navigation must be suppressed until unload. Only `resetAuthStatus` leaves
 * them, and only tests and a fresh boot call that.
 */
export const isNavigationLatched = (): boolean =>
  status.phase === "redirecting" || status.phase === "loggingOut";

export const isTerminal = (): boolean =>
  status.phase === "blocked" || status.phase === "failed";

export const setAuthStatus = (next: AuthStatus): void => {
  if (
    status.phase === next.phase &&
    status.reason === next.reason &&
    status.detail === next.detail
  ) {
    return;
  }
  status = next;
  for (const listener of [...listeners]) {
    listener(status);
  }
};

export const subscribeAuthStatus = (listener: Listener): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

export const resetAuthStatus = (): void => {
  status = { phase: "initializing" };
  listeners.clear();
};
